<template>
  <el-container class="layout-container">
    <el-aside width="200px" class="layout-aside">
      <div class="logo">后台管理</div>
      <el-menu
          :default-active="activeMenu"
          class="el-menu-vertical-demo"
          router
          background-color="#545c64"
          text-color="#fff"
          active-text-color="#ffd04b"
      >
        <template v-for="route in menuRoutes" :key="route.path">
          <!-- 只显示一级菜单，不显示子菜单 -->
          <el-menu-item :index="resolvePath(route.path)">
            <el-icon><component :is="route.meta.icon" /></el-icon>
            <span>{{ $t(route.meta.title) }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </el-aside>
    <el-container>
      <el-header class="layout-header">
        <div class="header-left">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item v-for="(item, index) in breadcrumbList" :key="index">
              {{ $t(item.meta.title) }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="header-right">
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              {{ userStore.userInfo?.username || '用户' }}
              <el-icon class="el-icon--right"><arrow-down /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="pwdModal = true" divided>{{ t('common.changePassword') }}</el-dropdown-item>
              </el-dropdown-menu>
              <el-dropdown-menu>
                <el-dropdown-item command="logout" divided>登出</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-select v-model="locale" @change="handleLanguageChange" style="width: 120px; margin-left: 20px;">
            <el-option label="繁体中文" value="tc" />
            <el-option label="简体中文" value="cn" />
            <el-option label="English" value="en" />
          </el-select>
        </div>
      </el-header>
      <el-main class="layout-main">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
  <el-dialog v-model="pwdModal" :title="t('common.changePassword')" width="500px">
    <change-password></change-password>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useI18n } from 'vue-i18n'
import ChangePassword from "@/views/system/ChangePassword.vue";

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { t, locale } = useI18n()
const pwdModal = ref(false)

/**
 * @20260105 新增permission区分账号，目前设置superAdmin，admin，pizu和hr，对应id为99,1,2,3
 * */
const menuRoutes = computed(() => {
  // 1. 找到Layout路由
  const layoutRoute = router.options.routes.find(r => r.name === 'Layout')
  // 打印用户ID，方便调试（可保留或删除）
  console.error('当前用户ID：', userStore.userInfo.id)

  // 2. 边界判断：没有Layout路由或子路由则返回空数组
  if (!layoutRoute || !layoutRoute.children) return []

  // 3. 改造过滤逻辑：同时满足「非隐藏」+「权限匹配」
  const filteredRoutes = layoutRoute.children.filter(r => {
    // 原有条件：路由不隐藏
    const isNotHidden = !r.meta.hidden
    if (!isNotHidden) return false // 隐藏路由直接过滤

    // 新增权限判断逻辑
    // 3.1 若路由没有配置permission字段，默认放行（可选：也可改为return false，根据业务定）
    if (!Array.isArray(r.meta.permission) || r.meta.permission.length === 0) {
      return true
    }

    // 3.2 获取当前用户ID（做空值保护，避免用户未登录报错）
    const userId = userStore.userInfo?.id
    // 3.3 校验：用户ID存在且在permission数组中
    return userId !== undefined && r.meta.permission.includes(userId)
  })

  // 打印过滤后的路由，方便调试（可保留或删除）
  console.error('过滤后的菜单路由：', filteredRoutes)
  return filteredRoutes
})

// 修复路径解析函数
const resolvePath = (basePath, childPath = '') => {
  // 确保 basePath 以 / 开头
  const normalizedBase = basePath.startsWith('/') ? basePath : `/${basePath}`

  if (!childPath) {
    return normalizedBase
  }

  // 处理子路径，移除可能的前导斜杠
  const normalizedChild = childPath.startsWith('/') ? childPath.slice(1) : childPath
  return `${normalizedBase}/${normalizedChild}`
}

const activeMenu = computed(() => {
  const { path } = route
  return path
})

const breadcrumbList = ref([])
watch(route, (newRoute) => {
  const matched = newRoute.matched.filter(item => item.meta && item.meta.title)
  breadcrumbList.value = matched
}, { immediate: true })

const handleLanguageChange = (val) => {
  locale.value = val
  localStorage.setItem('lang', val)
}

const handleCommand = (command) => {
  if (command === 'logout') {
    userStore.logout()
    router.push('/login')
  }
}
</script>

<style scoped>
.layout-container {
  height: 100vh;
}

.layout-aside {
  background-color: #545c64;
  color: #fff;
  transition: width 0.3s;
}

.logo {
  height: 60px;
  line-height: 60px;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  color: #ffd04b;
}

.el-menu-vertical-demo:not(.el-menu--collapse) {
  width: 200px;
  min-height: 400px;
}

.layout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  border-bottom: 1px solid #eee;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
}

.el-dropdown-link {
  cursor: pointer;
  color: var(--el-color-primary);
  display: flex;
  align-items: center;
}

.layout-main {
  background-color: #f0f2f5;
  padding: 20px;
}
</style>